import React, { useState } from 'react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { billAPI } from '../services/api';
import { Loader2, CheckCircle, XCircle, Search } from 'lucide-react';
import { cn } from '../lib/utils';

const WarrantyCheckModal = ({ onClose }) => {
    const [sku, setSku] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const handleCheck = async (e) => {
        e.preventDefault();
        if (!sku.trim()) return;

        setLoading(true);
        setError(null);
        setResult(null);

        try {
            const response = await billAPI.checkWarrantyStatus(sku);
            setResult(response.data);
        } catch (err) {
            console.error('Warranty check failed:', err);
            setError(err.response?.data?.message || 'Failed to check warranty. Please verify the SKU.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={true} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Check Warranty Status</DialogTitle>
                    <DialogDescription>
                        Enter the Serial Number (SKU) to check its warranty validity.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                    <form onSubmit={handleCheck} className="flex gap-2">
                        <Input
                            value={sku}
                            onChange={(e) => setSku(e.target.value)}
                            placeholder="Enter SKU / Serial Number"
                            className="flex-1"
                        />
                        <Button type="submit" disabled={loading || !sku.trim()}>
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                        </Button>
                    </form>

                    {error && (
                        <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm flex items-center gap-2">
                            <XCircle className="h-4 w-4" />
                            {error}
                        </div>
                    )}

                    {result && (
                        <div className={cn(
                            "p-4 rounded-lg border flex flex-col items-center justify-center text-center gap-2",
                            result.status === 'IN_WARRANTY' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-red-700'
                        )}>
                            {result.status === 'IN_WARRANTY' ? (
                                <CheckCircle className="h-8 w-8 mb-1" />
                            ) : (
                                <XCircle className="h-8 w-8 mb-1" />
                            )}
                            <h3 className="font-bold text-lg">
                                {result.status === 'IN_WARRANTY' ? 'In Warranty' : 'Expired / Not Found'}
                            </h3>
                            <p className="text-sm opacity-90">{result.message}</p>
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose}>Close</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default WarrantyCheckModal;
